'use client'
import { yupResolver } from '@hookform/resolvers/yup'
import cn from 'classnames'
import { FunctionComponent, useCallback } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import { object } from 'yup'

import { useCaptcha } from '@/hooks/Captcha'
import { acceptTermsValidator, phoneValidator } from '@/utils/validator'

import { AcceptCheckbox } from '@/components/AcceptCheckbox'
import { Button } from '@/components/Button'
import { FormInput } from '@/components/FormInput'
import { Loader } from '@/components/Loader'

import { getHelp } from '@/app/actions/help/actions'
import { marketingStorage } from '@/store/MarketingStorage'
import { HelpTypeSite } from '@/services/Api/generated'

import { YANDEX_METRIKA, YANDEX_METRIKA_COUNTER } from '@/config'
import { maxPopupStore, maxRequestFormTexts } from '@/store/MaxPopup.store'
import styles from './HelpExpertForm.module.scss'

interface IFormData {
  phone: string
  accept: boolean
}

const schema = object().shape({
  phone: phoneValidator,
  accept: acceptTermsValidator,
})

type HelpExpertFormProps = {
  className?: string
}
export const HelpExpertForm: FunctionComponent<HelpExpertFormProps> = ({
  className,
}) => {
  const { getToken } = useCaptcha()
  const {
    register,
    setValue,
    handleSubmit,
    formState: { isSubmitting, errors },
  } = useForm<IFormData>({
    mode: 'onChange',
    resolver: yupResolver(schema),
  })

  const onSubmit: SubmitHandler<IFormData> = useCallback(
    async (data) => {
      const captchaToken = await getToken()

      const response = await getHelp(
        {
          type: HelpTypeSite.HelpLawyer,
          data: {
            sourceUrl: window.location.href.split('?')[0],
            phone: data.phone,
            utmClientId: marketingStorage.getClientId() ?? undefined,
          },
        },
        captchaToken
      )
      if (response.status) {
        marketingStorage.clearClientId()
        if (YANDEX_METRIKA) {
          // @ts-ignore
          ym(YANDEX_METRIKA_COUNTER, 'reachGoal', 'FORM_HELP_EXPERT_SUBMIT')
        }

        setValue('phone', '')
        setValue('accept', false)

        maxPopupStore.show({
          sourceSite: response.data.params,
          ...maxRequestFormTexts,
        })
      } else {
        toast.error('Что то пошло не так! Попробуйте позже')
      }
    },
    [setValue, getToken]
  )

  return (
    <form
      className={cn(styles.container, className)}
      onSubmit={handleSubmit(onSubmit)}
    >
      <div className={styles.title}>Нужна помощь эксперта?</div>
      <div className={styles.content}>
        <div className={styles.description}>
          Оставьте номер телефона, мы перезвоним и проконсультируем по всем
          интересующим вас вопросам
        </div>
        <div className={styles.form}>
          <FormInput
            mask="+{7} (000) 000-00-00"
            placeholder="+7 (999) 999-99-99"
            className={styles.input}
            error={errors.phone?.message}
            {...register('phone')}
          />
          <Button
            type="submit"
            className={styles.button}
            disabled={isSubmitting}
            compactHover
          >
            Отправить
            {isSubmitting ? <Loader onTop /> : null}
          </Button>
        </div>
        <AcceptCheckbox<IFormData>
          className={styles.agree}
          register={register}
          error={errors.accept?.message}
        />
      </div>
      <div className={styles.overflow}>
        <img src="/images/dots.svg" className={styles.dots} alt="" />
      </div>
    </form>
  )
}
